import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

if (!customElements.get('lovirs-scarf-bundle')) {
  customElements.define('lovirs-scarf-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      try {
        const data = JSON.parse(this.querySelector('[data-scarf-variants]').textContent);
        this.variants = (data.variants || []).filter((variant) => variant.available);
      } catch (error) {
        this.showError('Color options could not be loaded. Please refresh the page.');
        return;
      }
      this.colors = [...new Set(this.variants.map((variant) => variant.color).filter(Boolean))];
      if (!this.variants.length || !this.colors.length) {
        this.showError('This item is currently unavailable.');
        return;
      }

      this.section = this.closest('.shopify-section') || document;
      this.details = this.closest('.product-details') || this.section;
      this.formComponent = this.details.querySelector('product-form-component') || this.section.querySelector('product-form-component');
      this.variantPicker = this.details.querySelector('variant-picker[data-product-id="' + this.dataset.productId + '"]');
      this.selectedQty = 1;
      this.baseColor = this.getCurrentVariant()?.color || this.colors[0];
      this.itemColors = {
        2: [this.baseColor, this.baseColor],
        3: [this.baseColor, this.baseColor, this.baseColor]
      };

      this.querySelectorAll('[data-scarf-tier]').forEach((button) => {
        button.addEventListener('click', () => this.selectTier(Number(button.dataset.scarfTier)));
      });

      this.onSubmit = (event) => {
        const form = event.target;
        if (!this.formComponent || !form || !form.matches('form[data-type="add-to-cart-form"]')) return;
        if (!this.formComponent.contains(form) || this.selectedQty <= 1) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.addBundle(false);
      };
      document.addEventListener('submit', this.onSubmit, true);

      this.onProductSelect = (event) => {
        if (event.promise && typeof event.promise.then === 'function') {
          event.promise.then(() => this.syncNativeColor()).catch(() => {});
        } else {
          setTimeout(() => this.syncNativeColor(), 0);
        }
      };
      this.section.addEventListener(StandardEvents.productSelect, this.onProductSelect);
      this.variantPicker?.addEventListener('change', () => setTimeout(() => this.syncNativeColor(), 0));

      this.resizeHandler = () => this.positionCheckoutOverlay();
      window.addEventListener('resize', this.resizeHandler);
      this.observer = new MutationObserver(() => this.syncAcceleratedCheckout());
      this.observer.observe(this.details, { childList: true, subtree: true });
      this.render();
      this.syncAcceleratedCheckout();
    }

    disconnectedCallback() {
      if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
      if (this.onProductSelect) this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
      if (this.observer) this.observer.disconnect();
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.acceleratedTarget) this.acceleratedTarget.inert = false;
    }

    getCurrentVariant() {
      const id = this.formComponent?.querySelector('input[name="id"]')?.value;
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    syncNativeColor() {
      const current = this.getCurrentVariant();
      if (!current || current.color === this.baseColor) return;
      if (this.selectedQty > 1) {
        const rows = this.itemColors[this.selectedQty] || [];
        if (rows.every((color) => color === this.baseColor)) {
          this.itemColors[this.selectedQty] = rows.map(() => current.color);
        }
      }
      this.baseColor = current.color;
      this.render();
    }

    selectTier(qty) {
      if (![1, 2, 3].includes(qty)) return;
      this.selectedQty = qty;
      if (qty > 1) {
        const existing = this.itemColors[qty] || [];
        this.itemColors[qty] = Array.from({ length: qty }, (_, index) => existing[index] || this.baseColor);
      }
      this.render();
    }

    render() {
      this.querySelectorAll('[data-scarf-card]').forEach((card) => {
        const qty = Number(card.dataset.scarfCard);
        const selected = qty === this.selectedQty;
        card.classList.toggle('is-selected', selected);
        const button = card.querySelector('[data-scarf-tier]');
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const options = card.querySelector('[data-scarf-options]');
        if (options) options.hidden = !selected || qty === 1;
      });

      if (this.selectedQty > 1) this.renderItemSelectors(this.selectedQty);
      const colors = this.selectedQty === 1 ? [this.baseColor] : this.itemColors[this.selectedQty];
      const colorText = colors.map((color) => this.titleCase(color)).join(' + ');
      const priceText = this.querySelector('[data-scarf-card="' + this.selectedQty + '"] [data-scarf-price]')?.textContent?.trim() || '';
      this.updateSummary();
      this.syncAcceleratedCheckout();
    }

    updateSummary() {
      const colors = this.selectedQty === 1 ? [this.baseColor] : (this.itemColors[this.selectedQty] || []);
      const colorText = colors.map((color) => this.titleCase(color)).join(' + ');
      const priceText = this.querySelector('[data-scarf-card="' + this.selectedQty + '"] [data-scarf-price]')?.textContent?.trim() || '';
      const summary = this.querySelector('[data-scarf-summary]');
      const total = this.querySelector('[data-scarf-summary-total]');
      if (summary) summary.textContent = colorText + ' · ' + this.selectedQty + (this.selectedQty === 1 ? ' scarf' : ' scarves');
      if (total) total.textContent = priceText + ' total';
    }

    renderItemSelectors(qty) {
      const wrap = this.querySelector('[data-scarf-item-selectors="' + qty + '"]');
      if (!wrap) return;
      wrap.replaceChildren();
      this.itemColors[qty].forEach((color, index) => {
        const variant = this.variants.find((item) => item.color === color);
        const row = document.createElement('div');
        row.className = 'lsb-item';

        const image = document.createElement('img');
        image.alt = 'Scarf ' + (index + 1);
        if (variant?.image) image.src = variant.image;
        row.appendChild(image);

        const field = document.createElement('div');
        const label = document.createElement('label');
        label.textContent = 'Scarf ' + (index + 1);
        const select = document.createElement('select');
        select.setAttribute('aria-label', 'Color for scarf ' + (index + 1));

        this.colors.forEach((optionColor) => {
          const option = document.createElement('option');
          option.value = optionColor;
          option.textContent = this.titleCase(optionColor);
          option.selected = optionColor === color;
          select.appendChild(option);
        });

        select.addEventListener('change', () => {
          this.itemColors[qty][index] = select.value;
          const selectedVariant = this.variants.find((item) => item.color === select.value);
          if (selectedVariant?.image) image.src = selectedVariant.image;
          this.updateSummary();
        });
        field.appendChild(label);
        field.appendChild(select);
        row.appendChild(field);
        wrap.appendChild(row);
      });
    }

    titleCase(value) {
      const text = String(value || '');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    syncAcceleratedCheckout() {
      const block = this.details.querySelector('.accelerated-checkout-block');
      if (!block) return;
      const target = block.querySelector('shopify-accelerated-checkout, .shopify-payment-button__button--branded, .shopify-payment-button__button, .shopify-payment-button');
      if (!target) return;

      this.acceleratedBlock = block;
      this.acceleratedTarget = target;
      if (!this.checkoutOverlay || !this.checkoutOverlay.isConnected) {
        this.checkoutOverlay = document.createElement('button');
        this.checkoutOverlay.type = 'button';
        this.checkoutOverlay.className = 'lsb-checkout-overlay';
        this.checkoutOverlay.setAttribute('aria-label', 'Continue to checkout with the selected scarf bundle');
        this.checkoutOverlay.hidden = true;
        this.checkoutOverlay.addEventListener('click', () => this.addBundle(true));
        block.appendChild(this.checkoutOverlay);
      }
      this.positionCheckoutOverlay();
      this.checkoutOverlay.hidden = this.selectedQty <= 1;
      this.acceleratedTarget.inert = this.selectedQty > 1;
    }

    positionCheckoutOverlay() {
      if (!this.acceleratedBlock || !this.acceleratedTarget || !this.checkoutOverlay) return;
      if (!this.acceleratedTarget.isConnected || !this.acceleratedBlock.isConnected) return;
      const targetRect = this.acceleratedTarget.getBoundingClientRect();
      const blockRect = this.acceleratedBlock.getBoundingClientRect();
      this.acceleratedBlock.style.position = 'relative';
      this.checkoutOverlay.style.top = (targetRect.top - blockRect.top + this.acceleratedBlock.scrollTop) + 'px';
      this.checkoutOverlay.style.left = (targetRect.left - blockRect.left + this.acceleratedBlock.scrollLeft) + 'px';
      this.checkoutOverlay.style.width = targetRect.width + 'px';
      this.checkoutOverlay.style.height = targetRect.height + 'px';
    }

    showError(message) {
      const node = this.querySelector('[data-scarf-error]');
      if (node) node.textContent = message;
    }

    async addBundle(buyNow) {
      const selectedColors = this.itemColors[this.selectedQty] || [];
      const colors = this.selectedQty === 1 ? [this.baseColor] : selectedColors;
      const selectedVariants = colors.map((color) => this.variants.find((variant) => variant.color === color));
      if (selectedVariants.length !== this.selectedQty || selectedVariants.some((variant) => !variant)) {
        this.showError('Please choose an available color for every scarf.');
        return;
      }

      const counts = new Map();
      selectedVariants.forEach((variant) => {
        const key = String(variant.id);
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const root = window.Shopify?.routes?.root || '/';
      const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);

      const submitButton = this.formComponent?.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;
      if (this.checkoutOverlay) this.checkoutOverlay.disabled = true;
      this.showError('');

      let deferred = null;
      if (!buyNow && this.formComponent) {
        deferred = CartLinesUpdateEvent.createPromise();
        this.formComponent.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise
        }));
      }

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: items,
            sections: cartSections.join(',')
          })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this bundle. Please try again.');
        }

        if (buyNow) {
          window.location.assign(root + 'checkout');
          return;
        }

        const cartResponse = await fetch(root + 'cart.js', {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        });
        if (!cartResponse.ok) throw new Error('Your items were added, but the cart could not be refreshed.');
        const cart = await cartResponse.json();
        deferred?.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.formComponent?.id || '',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: result.sections,
            didError: false
          }
        });
      } catch (error) {
        const message = error?.message || 'Unable to add this bundle. Please try again.';
        this.showError(message);
        if (deferred) deferred.reject(error);
        if (this.formComponent) {
          this.formComponent.dispatchEvent(new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: message }
          }));
        }
      } finally {
        if (submitButton) submitButton.disabled = false;
        if (this.checkoutOverlay) this.checkoutOverlay.disabled = false;
      }
    }
  });
}
